import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, CircleDashed, EyeOff, Moon, PauseCircle, XCircle } from 'lucide-react';
import { Chip } from '@nyce/admin-kit/ui';
import { ApiError } from '@nyce/admin-kit/api';
import { Tabs } from '../../components/common';
import { createUseParam } from '@nyce/admin-kit/console';
import type { EntryStatus, InstallationState, OrgStatus } from '../../api/stats';
import type { Tone } from '../../components/status';

// Building blocks of the Statistics pages (A4): URL state, time ranges, status chips, formatting, links.

export const orgPath = (customerId: number | null | undefined, tab?: string) =>
  `/statistics/orgs/${customerId ?? ''}${tab ? `?tab=${tab}` : ''}`;
export const installationPath = (customerId: number, workserver: string, tab?: string) =>
  `/statistics/installations/${customerId}/${encodeURIComponent(workserver)}${tab ? `?tab=${tab}` : ''}`;

/**
 * One URL query parameter as state (filters and tabs are shareable); setting the default removes it. Several setters
 * called in one handler all apply (the kit's `createUseParam` over react-router).
 */
export const useParam = createUseParam(useSearchParams);

// ---------------------------------------------------------------------------------------------------- time ranges

export type RangeKey = '1h' | '4h' | '12h' | '24h' | '7d' | '30d' | '90d' | '1y';
export const RANGE_OPTIONS: { value: RangeKey; label: string }[] = [
  { value: '24h', label: '24 h' },
  { value: '7d', label: '7 d' },
  { value: '30d', label: '30 d' },
  { value: '90d', label: '90 d' },
  { value: '1y', label: '1 y' },
];
/** Short presets, only where minute data exists (the Performance tab). */
export const SHORT_RANGES: { value: RangeKey; label: string }[] = [
  { value: '1h', label: '1 h' },
  { value: '4h', label: '4 h' },
  { value: '12h', label: '12 h' },
];
const RANGE_HOURS: Record<RangeKey, number> = {
  '1h': 1,
  '4h': 4,
  '12h': 12,
  '24h': 24,
  '7d': 168,
  '30d': 720,
  '90d': 2160,
  '1y': 8760,
};
export const isShortRange = (key: RangeKey) => RANGE_HOURS[key] < 24;

export interface Window {
  from: string;
  to: string;
  /** Inclusive UTC days for the daily endpoints. */
  fromDay: string;
  toDay: string;
  /** Hourly rows up to 7 days (the collector allows 31), daily beyond. */
  bucket: '1h' | '1d';
  hours: number;
}

/**
 * The window ending now: to the next full hour, so repeated loads hit the same rows; a short preset to the next
 * minute (its points are minutes).
 */
export function rangeWindow(key: RangeKey, now: number = Date.now()): Window {
  const hours = RANGE_HOURS[key] ?? 168;
  const unit = hours < 24 ? 60_000 : 3_600_000;
  const to = new Date(Math.ceil(now / unit) * unit);
  const from = new Date(to.getTime() - hours * 3_600_000);
  return {
    from: from.toISOString(),
    to: to.toISOString(),
    fromDay: from.toISOString().slice(0, 10),
    toDay: new Date(to.getTime() - 1).toISOString().slice(0, 10),
    bucket: hours <= 168 ? '1h' : '1d',
    hours,
  };
}

/** Range picker bound to `?range=`; `short` = the view offers 1 h / 4 h / 12 h. `max` drops presets an endpoint cannot serve. */
export function useRange(fallback: RangeKey = '7d', short = false): [RangeKey, (v: RangeKey) => void] {
  const [range, setRange] = useParam<RangeKey>('range', fallback);
  // a short preset left in the link by the Performance tab is not one this view offers
  return [!short && isShortRange(range) ? fallback : range, setRange];
}

export function RangeTabs({
  value,
  onChange,
  max,
  short,
}: {
  value: RangeKey;
  onChange: (v: RangeKey) => void;
  max?: RangeKey;
  /** Offer the 1 h / 4 h / 12 h presets too. */
  short?: boolean;
}) {
  const limit = max ? RANGE_HOURS[max] : Infinity;
  return (
    <Tabs
      value={value}
      onChange={onChange}
      options={[...(short ? SHORT_RANGES : []), ...RANGE_OPTIONS].filter(
        (o) => RANGE_HOURS[o.value] <= limit
      )}
    />
  );
}

/**
 * A counter that goes up every `ms` while `on` and the page is visible, and when the page becomes visible again:
 * put it in a load's deps to refresh it.
 */
export function useRefreshTick(ms: number, on: boolean) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!on) return;
    const bump = () => {
      if (typeof document === 'undefined' || !document.hidden) setTick((n) => n + 1);
    };
    const id = setInterval(bump, ms);
    document.addEventListener('visibilitychange', bump);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', bump);
    };
  }, [ms, on]);
  return tick;
}

// ------------------------------------------------------------------------------------------------------ formatting

/** Durations in milliseconds: `850 ms`, `1.24 s`, `12.5 s`. */
export function fmtMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const s = ms / 1000;
  return `${s >= 100 ? Math.round(s) : Number(s.toFixed(s >= 10 ? 1 : 2))} s`;
}

/** `1 234 567` → `1 234 567` with the user's separators. */
export const fmtInt = (n: number | null | undefined) => (n == null ? '—' : Math.round(n).toLocaleString());

export const fmtDay = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });

/** The collector's message for a range that is too long, else the error text. */
export const statsError = (e: unknown): string =>
  e instanceof ApiError && e.code === 'range_too_large'
    ? `${e.message} Pick a shorter range.`
    : (e as Error)?.message || String(e);

// ------------------------------------------------------------------------------------------------- status chips

const ORG: Record<OrgStatus, { label: string; tone: Tone; icon: ReactNode }> = {
  ok: { label: 'Reporting', tone: 'green', icon: <CheckCircle2 size={14} /> },
  partial: { label: 'Partly silent', tone: 'amber', icon: <AlertTriangle size={14} /> },
  silent: { label: 'Silent', tone: 'red', icon: <XCircle size={14} /> },
  never: { label: 'Never seen', tone: 'grey', icon: <CircleDashed size={14} /> },
  inactive: { label: 'Monitoring off', tone: 'grey', icon: <PauseCircle size={14} /> },
  ignored: { label: 'Not monitored', tone: 'grey', icon: <EyeOff size={14} /> },
};

const MARKED = { label: 'Marked inactive', tone: 'grey' as Tone, icon: <PauseCircle size={14} /> };
const IDLE = { label: 'Idle', tone: 'green' as Tone, icon: <Moon size={14} /> };

/**
 * Organization or installation ingest status, icon + label (never colour alone). `inactive` is "Monitoring off" for an
 * organization whose monitoring is off, else an operator's mark (`monitoring` true, or an installation). An `ok`
 * installation that is `idle` (no timings, but outside its usual hours) shows as Idle.
 */
export function IngestStatus({
  status,
  monitoring,
  idle,
}: {
  status: OrgStatus | InstallationState;
  monitoring?: boolean;
  idle?: boolean;
}) {
  const m =
    status === 'inactive' && monitoring
      ? MARKED
      : status === 'ok' && idle
        ? IDLE
        : (ORG[status] ?? ORG.never);
  return (
    <Chip
      size="sm"
      className={`oph-status nyk-tone-${m.tone}`}
      data-status={status}
      title={
        m === IDLE ? 'No method timings, but only in hours it usually has none (nights, weekends)' : undefined
      }
    >
      {m.icon}
      {m.label}
    </Chip>
  );
}

const ENTRY: Record<EntryStatus, { label: string; tone: Tone | 'blue' }> = {
  open: { label: 'Open', tone: 'amber' },
  acknowledged: { label: 'Acknowledged', tone: 'blue' },
  closed: { label: 'Closed', tone: 'grey' },
};

export function EntryStatusChip({ status }: { status: EntryStatus }) {
  const m = ENTRY[status] ?? ENTRY.open;
  return (
    <Chip size="sm" className={`oph-status nyk-tone-${m.tone}`} data-status={status}>
      {m.label}
    </Chip>
  );
}

/** WMS log severity (0..9 as PMS stores it). */
export const SEVERITIES: Record<number, string> = {
  0: 'Trace',
  1: 'Debug',
  2: 'Info',
  3: 'Warning',
  4: 'Error',
  5: 'Critical',
};
export const severityLabel = (s: number | null | undefined) =>
  s == null ? '—' : (SEVERITIES[s] ?? String(s));

export function OrgLink({ customerId, name }: { customerId: number | null; name: string | null }) {
  if (customerId == null) return <>{name ?? '—'}</>;
  return (
    <Link to={orgPath(customerId)} className="nyk-link">
      {name || `Customer ${customerId}`}
    </Link>
  );
}

/** `Sys:CPU` → `CPU`. */
export const statName = (s: string) => s.replace(/^Sys:/i, '');
